import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import WarningBox, { WarningBoxColorType } from 'src/components/ui/WarningBox/WarningBox';
import Divider from 'src/components/ui/Divider/Divider';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import { useInboundOrder } from 'src/hooks/useInboundOrder';
import { ActionBar, InboundConfiguratorForm, Section } from './InboundConfiguratorForm';
import InboundBackordersCard from './InboundBackordersCard';
import InboundConfirmDialog from './InboundConfirmDialog';
import { DownloadSection, InboundCreateShell } from './InboundCreateData';
import { inboundOrderToFormValues, useInboundFormContext } from './InboundFormContext';
import { getInboundDeliverySlip, useInboundOrdersQuery } from '../../services/inbound';
import { formatDate, Status } from '../../utils/general';
import { getInboundNavIds } from '../../utils/inboundNav';
import OverlayNavArrows from '../../components/layouts/OverlayNavArrows/OverlayNavArrows';
import { OverlayNavProvider } from '../../providers/OverlayNavProvider';

// Inbound-Draft spec 2026-11-03 §6: the detail page is the draft's editor
// (readOnly toggle of the configurator), confirm/cancel/draft/duplicate run here.
const InboundCreateConfirm: React.FC<{}> = () => {
  const { id: idString } = useParams();
  const id = Number.parseInt(idString ?? '', 10);

  if (!idString || Number.isNaN(id)) {
    throw new Error('id is required');
  }
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef<{ triggerSubmit: () => void }>(null);
  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    itemsPerPage: 10,
  });

  const { currentPage, itemsPerPage } = paginationState;

  const query = React.useMemo(
    () => ({ offset: (currentPage - 1) * itemsPerPage, limit: itemsPerPage }),
    [currentPage, itemsPerPage],
  );

  const {
    order: inboundOrder,
    isLoading,
    edit,
    isBusy,
    canEdit,
    canConfirm,
    canCancel,
    canDraft,
    isCancelRequested,
    rejectedRequest,
    startEdit,
    requestExitEdit,
    isExitDialogOpen,
    handleExitDialog,
    setDirty,
    save,
    confirm,
    cancelOrder,
    setToDraft,
    duplicate,
  } = useInboundOrder(id, query);

  // Ids for the prev/next arrow navigation. Primary source: the shared store the
  // inbound list fills on load (respects the user's filter/sort). Fallback for a
  // hard refresh / deep-link of this page (where the list never mounted): fetch
  // the default inbound list so the arrows still work.
  const storeNavIds = getInboundNavIds();
  const { data: inboundListFallback } = useInboundOrdersQuery(
    { page: 1, size: 200 },
    { enabled: storeNavIds.length === 0 },
  );
  const navIds = storeNavIds.length
    ? storeNavIds
    : (inboundListFallback?.items ?? []).map(o => o.id).filter((x): x is number => x != null);

  const handlePaginationChange = (page: number, perPage: number) => {
    const maxPages = inboundOrder?.total_pages ?? Infinity;
    const clampedPage = Math.max(1, Math.min(page, maxPages));
    setPaginationState({ currentPage: clampedPage, itemsPerPage: perPage });
  };

  const { formState, setFormState } = useInboundFormContext();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const success = queryParams.get('success') === '1';
  const created = queryParams.get('created') === '1' && !success;

  useEffect(() => {
    // while editing, the form owns the values — do not overwrite the buffer
    if (inboundOrder && !edit) {
      setFormState(inboundOrderToFormValues(inboundOrder));
    }
  }, [inboundOrder, edit]);

  useEffect(() => {
    if (inboundOrder?.packages) {
      const maxPages = Math.ceil(inboundOrder.packages.length / paginationState.itemsPerPage);
      if (paginationState.currentPage > maxPages && maxPages > 0) {
        setPaginationState(prev => ({
          ...prev,
          currentPage: maxPages,
        }));
      }
    }
  }, [inboundOrder?.packages, paginationState.itemsPerPage]);

  const stateKey = inboundOrder?.inbound_state?.key ?? undefined;
  const showDownload = !!inboundOrder && stateKey !== Status.DRAFT && stateKey !== Status.CANCEL;
  const wizard = success || created;
  const title = wizard ? `${t('inbound.inbound')} / ${t('inbound.create_cta')}` : t('inbound.inbound');
  const headline = wizard ? t('inbound.create_headline') : t('inbound.inbound');

  const handleConfirmDialog = async (confirmed: boolean) => {
    setConfirmDialogOpen(false);
    if (!confirmed) return;
    if (await confirm()) {
      document.querySelector('.main-content-wrapper')?.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/warehouse/inbound/${id}?success=1`);
    }
  };

  const handleCancelDialog = async (confirmed: boolean) => {
    setCancelDialogOpen(false);
    if (confirmed) await cancelOrder();
  };

  const handleDraftDialog = async (confirmed: boolean) => {
    setDraftDialogOpen(false);
    if (confirmed) await setToDraft();
  };

  const handleDuplicate = async () => {
    const copy = await duplicate();
    if (copy?.id) navigate(`/warehouse/inbound/${copy.id}?created=1`);
  };

  const handleDownload = async () => {
    const fileName = `order_${id}_delivery_slip.pdf`;
    setDownloadLoading(true);
    let blob;
    try {
      blob = await getInboundDeliverySlip(id);
    } catch (e) {
      setDownloadLoading(false);
    }
    if (!blob) {
      setDownloadLoading(false);
      throw new Error('Failed to download delivery slip');
    }
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setDownloadLoading(false);
  };

  return (
    !isLoading && (
      <OverlayNavProvider entityIds={navIds} basePath="/warehouse/inbound">
        <InboundCreateShell
          step={wizard ? 3 : undefined}
          title={title}
          headline={headline}
          orderId={id}
          status={inboundOrder?.inbound_state}
          headerActions={<OverlayNavArrows />}>
          <div className="flex flex-col gap-y-20">
            {success && (
              <WarningBox
                colorType={WarningBoxColorType.Successful}
                title={t('inbound.success_title')}
                copy={t('inbound.success_copy')}
              />
            )}
            {created && (
              <WarningBox
                colorType={WarningBoxColorType.Neutral}
                title={t('inbound.draft_created_title')}
                copy={t('inbound.draft_created_copy')}
              />
            )}
            {isCancelRequested && (
              <WarningBox
                colorType={WarningBoxColorType.Warning}
                icon="warning"
                title={t('inbound.cancel_requested_banner_title')}
                copy={t('inbound.cancel_requested_hint')}
              />
            )}
            {rejectedRequest && (
              <WarningBox
                colorType={WarningBoxColorType.Error}
                icon="warning"
                title={t('inbound.cancel_rejected_banner_title')}
                copy={t('inbound.cancel_rejected_banner_copy', {
                  date: rejectedRequest.at ? formatDate(rejectedRequest.at) : '',
                  reason: rejectedRequest.reason ?? '',
                })}
              />
            )}
            {stateKey === Status.DRAFT && !canEdit && (
              <WarningBox
                colorType={WarningBoxColorType.Neutral}
                title={t('inbound.draft_backend_hint_title')}
                copy={t('inbound.draft_backend_hint')}
              />
            )}
            {showDownload && (
              <DownloadSection
                loading={downloadLoading}
                title={t('inbound.delivery_slip_download_title')}
                copy={t('inbound.delivery_slip_download_copy')}
                onDownloadClick={handleDownload}
              />
            )}

            <Section className="flex flex-col gap-y-32">
              <InboundConfiguratorForm
                ref={formRef}
                readOnly={!edit}
                onSubmit={data => {
                  save({ data, summary: formState.summary });
                }}
                onDirtyChange={setDirty}
                onPaginationChange={handlePaginationChange}
                currentPage={paginationState.currentPage}
                itemsPerPage={paginationState.itemsPerPage}
                totalPages={Math.ceil(Number(inboundOrder?.total_pages) / paginationState.itemsPerPage)}
                inboundOrder={inboundOrder}
              />
              {inboundOrder && !edit && <InboundBackordersCard inboundOrder={inboundOrder} />}
              <Divider className="" />
              <ActionBar>
                {edit ? (
                  <>
                    <Button
                      title={t('inbound.cancel_edit')}
                      tone={ButtonToneProps.SECONDARY}
                      onClick={requestExitEdit}
                      disabled={isBusy}
                    />
                    <Button
                      title={t('inbound.save')}
                      tone={ButtonToneProps.PRIMARY_BLUE}
                      onClick={() => formRef.current?.triggerSubmit()}
                      disabled={isBusy}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      title={t('inbound.to_overview')}
                      tone={ButtonToneProps.GHOST}
                      onClick={() => {
                        navigate('/warehouse/inbound');
                      }}
                    />
                    <Button
                      title={t('inbound.duplicate')}
                      tone={ButtonToneProps.SECONDARY}
                      onClick={handleDuplicate}
                      disabled={isBusy}
                    />
                    {canDraft && (
                      <Button
                        title={t('inbound.draft_cta')}
                        tone={ButtonToneProps.SECONDARY}
                        onClick={() => setDraftDialogOpen(true)}
                        disabled={isBusy}
                      />
                    )}
                    {canCancel && (
                      <Button
                        title={t('inbound.cancel')}
                        tone={ButtonToneProps.ERROR}
                        onClick={() => setCancelDialogOpen(true)}
                        disabled={isBusy}
                      />
                    )}
                    {canEdit && (
                      <Button
                        title={t('inbound.edit')}
                        tone={ButtonToneProps.SECONDARY}
                        onClick={startEdit}
                        disabled={isBusy}
                      />
                    )}
                    {canConfirm && (
                      <Button
                        title={t('inbound.confirm_cta')}
                        tone={ButtonToneProps.PRIMARY_BLUE}
                        onClick={() => setConfirmDialogOpen(true)}
                        disabled={isBusy}
                      />
                    )}
                  </>
                )}
              </ActionBar>
            </Section>
          </div>
          <ConfirmationModal
            isOpen={cancelDialogOpen}
            onClose={handleCancelDialog}
            title={t('inbound.cancel_dialog_title')}
            description={t('inbound.cancel_dialog_copy')}
          />
          <ConfirmationModal
            isOpen={draftDialogOpen}
            onClose={handleDraftDialog}
            title={t('inbound.draft_dialog_title')}
            description={t('inbound.draft_dialog_copy')}
          />
          <ConfirmationModal
            isOpen={isExitDialogOpen}
            onClose={handleExitDialog}
            title={t('inbound.exit_edit_title')}
            description={t('inbound.exit_edit_copy')}
          />
          <InboundConfirmDialog isOpen={confirmDialogOpen} onClose={handleConfirmDialog} isBusy={isBusy} />
        </InboundCreateShell>
      </OverlayNavProvider>
    )
  );
};

export default InboundCreateConfirm;
